import { defineMessages, type MessageDescriptor } from 'react-intl';
import type { MusicVideoEffectFamily, MusicVideoEffectKind } from 'fym-shared/src/music24/musicVideoEffects';

/** The copy of the effects lane, the effect inspector and the add menu (spec 2026-10-17 directed video §8). */
export const effectMessages = defineMessages({
  lane: { id: 'app.sml.musicVideo.effects.lane', defaultMessage: 'Effects' },
  blockLabel: { id: 'app.sml.musicVideo.effects.blockLabel', defaultMessage: '{name}, {from} to {to}' },
  blockTitle: { id: 'app.sml.musicVideo.effects.blockTitle', defaultMessage: '{name} · Drag to move it' },
  resizeLabel: { id: 'app.sml.musicVideo.effects.resizeLabel', defaultMessage: 'Change the end of {name}' },
  addTitle: { id: 'app.sml.musicVideo.effects.addTitle', defaultMessage: 'Effects' },
  addEffect: { id: 'app.sml.musicVideo.effects.addEffect', defaultMessage: '+ Add effect' },
  addAt: { id: 'app.sml.musicVideo.effects.addAt', defaultMessage: 'Adds at {time}' },
  addAtSection: { id: 'app.sml.musicVideo.effects.addAtSection', defaultMessage: 'Adds at {time}, in {section}' },
  addSearch: { id: 'app.sml.musicVideo.effects.addSearch', defaultMessage: 'Find an effect' },
  addNone: { id: 'app.sml.musicVideo.effects.addNone', defaultMessage: 'No effect has this name.' },
  addFromBar: {
    id: 'app.sml.musicVideo.effects.addFromBar',
    defaultMessage: 'Add one with “+ Add effect” in the timeline bar.',
  },
  addCount: {
    id: 'app.sml.musicVideo.effects.addCount',
    defaultMessage: '{count, plural, one {# effect} other {# effects}} in this cut',
  },
  addFull: {
    id: 'app.sml.musicVideo.effects.addFull',
    defaultMessage: 'This cut has the most effects it can hold.',
  },
  starts: { id: 'app.sml.musicVideo.effects.starts', defaultMessage: 'Starts' },
  ends: { id: 'app.sml.musicVideo.effects.ends', defaultMessage: 'Ends' },
  setToPlayHead: { id: 'app.sml.musicVideo.effects.setToPlayHead', defaultMessage: 'Set to the play head' },
  lengthHint: {
    id: 'app.sml.musicVideo.effects.lengthHint',
    defaultMessage: 'This effect lasts {min} to {max} seconds.',
  },
  remove: { id: 'app.sml.musicVideo.effects.remove', defaultMessage: 'Remove the effect' },
  previewHint: {
    id: 'app.sml.musicVideo.effects.previewHint',
    defaultMessage: 'The preview shows a sketch of the effect. The video file shows it in full.',
  },
  textSongTitle: { id: 'app.sml.musicVideo.effects.textSongTitle', defaultMessage: 'Empty: the song title' },
  textArtist: { id: 'app.sml.musicVideo.effects.textArtist', defaultMessage: 'Empty: the artist name' },
  textChapter: { id: 'app.sml.musicVideo.effects.textChapter', defaultMessage: 'A few words, for example "Part II"' },
});

/** The name of each kind (one static id for each kind, so the extraction finds every one). */
export const effectKindMessages: Record<MusicVideoEffectKind, MessageDescriptor> = defineMessages({
  lyricKinetic: { id: 'app.sml.musicVideo.effects.kind.lyricKinetic', defaultMessage: 'Kinetic lyric' },
  lyricHook: { id: 'app.sml.musicVideo.effects.kind.lyricHook', defaultMessage: 'Big hook lyric' },
  titleCard: { id: 'app.sml.musicVideo.effects.kind.titleCard', defaultMessage: 'Title card' },
  artistCard: { id: 'app.sml.musicVideo.effects.kind.artistCard', defaultMessage: 'Artist card' },
  chapterCard: { id: 'app.sml.musicVideo.effects.kind.chapterCard', defaultMessage: 'Chapter card' },
  whip: { id: 'app.sml.musicVideo.effects.kind.whip', defaultMessage: 'Whip pan' },
  flashCut: { id: 'app.sml.musicVideo.effects.kind.flashCut', defaultMessage: 'Flash' },
  filmBurn: { id: 'app.sml.musicVideo.effects.kind.filmBurn', defaultMessage: 'Film burn' },
  glitch: { id: 'app.sml.musicVideo.effects.kind.glitch', defaultMessage: 'Glitch' },
  zoomThrough: { id: 'app.sml.musicVideo.effects.kind.zoomThrough', defaultMessage: 'Zoom through' },
  splitScreen: { id: 'app.sml.musicVideo.effects.kind.splitScreen', defaultMessage: 'Split screen' },
  freezeStroke: { id: 'app.sml.musicVideo.effects.kind.freezeStroke', defaultMessage: 'Freeze and outline' },
  rgbSplit: { id: 'app.sml.musicVideo.effects.kind.rgbSplit', defaultMessage: 'Colour split' },
  beatShake: { id: 'app.sml.musicVideo.effects.kind.beatShake', defaultMessage: 'Beat shake' },
  colourPulse: { id: 'app.sml.musicVideo.effects.kind.colourPulse', defaultMessage: 'Colour pulse' },
});

/** The name of each family. */
export const effectFamilyMessages: Record<MusicVideoEffectFamily, MessageDescriptor> = defineMessages({
  lyric: { id: 'app.sml.musicVideo.effects.family.lyric', defaultMessage: 'Lyric moments' },
  title: { id: 'app.sml.musicVideo.effects.family.title', defaultMessage: 'Text cards' },
  transition: { id: 'app.sml.musicVideo.effects.family.transition', defaultMessage: 'Transitions' },
  shot: { id: 'app.sml.musicVideo.effects.family.shot', defaultMessage: 'Shot effects' },
});

/**
 * The label of each param key of the catalogue. A key with no entry shows the key itself, so a new
 * kind with a new param still gets its control.
 */
export const effectParamMessages: Partial<Record<string, MessageDescriptor>> = defineMessages({
  intensity: { id: 'app.sml.musicVideo.effects.param.intensity', defaultMessage: 'Strength' },
  colour: { id: 'app.sml.musicVideo.effects.param.colour', defaultMessage: 'Colour' },
  place: { id: 'app.sml.musicVideo.effects.param.place', defaultMessage: 'Place' },
  scale: { id: 'app.sml.musicVideo.effects.param.scale', defaultMessage: 'Size' },
  text: { id: 'app.sml.musicVideo.effects.param.text', defaultMessage: 'Text' },
  layout: { id: 'app.sml.musicVideo.effects.param.layout', defaultMessage: 'Layout' },
  direction: { id: 'app.sml.musicVideo.effects.param.direction', defaultMessage: 'Direction' },
});

/** The label of each enum value of the catalogue. A value with no entry shows the value itself. */
export const effectValueMessages: Partial<Record<string, MessageDescriptor>> = defineMessages({
  accent: { id: 'app.sml.musicVideo.effects.value.accent', defaultMessage: 'Theme accent' },
  white: { id: 'app.sml.musicVideo.effects.value.white', defaultMessage: 'White' },
  black: { id: 'app.sml.musicVideo.effects.value.black', defaultMessage: 'Black' },
  center: { id: 'app.sml.musicVideo.effects.value.center', defaultMessage: 'Middle' },
  top: { id: 'app.sml.musicVideo.effects.value.top', defaultMessage: 'Top' },
  bottom: { id: 'app.sml.musicVideo.effects.value.bottom', defaultMessage: 'Bottom' },
  lowerThird: { id: 'app.sml.musicVideo.effects.value.lowerThird', defaultMessage: 'Lower third' },
  corner: { id: 'app.sml.musicVideo.effects.value.corner', defaultMessage: 'Corner' },
  left: { id: 'app.sml.musicVideo.effects.value.left', defaultMessage: 'Left' },
  right: { id: 'app.sml.musicVideo.effects.value.right', defaultMessage: 'Right' },
  up: { id: 'app.sml.musicVideo.effects.value.up', defaultMessage: 'Up' },
  down: { id: 'app.sml.musicVideo.effects.value.down', defaultMessage: 'Down' },
  in: { id: 'app.sml.musicVideo.effects.value.in', defaultMessage: 'Zoom in' },
  out: { id: 'app.sml.musicVideo.effects.value.out', defaultMessage: 'Zoom out' },
  halves: { id: 'app.sml.musicVideo.effects.value.halves', defaultMessage: 'Two panels' },
  thirds: { id: 'app.sml.musicVideo.effects.value.thirds', defaultMessage: 'Three panels' },
  grid: { id: 'app.sml.musicVideo.effects.value.grid', defaultMessage: 'Four panels' },
});
